import axios from 'axios';
import { mutate } from 'swr';

import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { getError } from '@shared/utils/helpers';

import { ICreateReasonRequest, IWorkflowStep, TWorkflowFormState } from './types';

const WORKFLOW_PATH = `/${SERVICES.WORKFLOW}/workflows`;
const STEP_PATH = `/${SERVICES.WORKFLOW}/steps`;
const STEP_REASON = `/${SERVICES.WORKFLOW}/steps/reason`;
const DELETE_REASON = `/${SERVICES.WORKFLOW}/reasons`;

export const requestAddNewWorkflow = (data: TWorkflowFormState) => {
  return mutate(WORKFLOW_PATH, async() => {
    try {
      await axios.post(WORKFLOW_PATH, data);
      Notistack.successMessage(`${data.name} - ${data.entityType} - workflow created successfully`);
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestRenameWorkflow = (id: string, name: string) => {
  return mutate(WORKFLOW_PATH, async() => {
    try {
      await axios.put(`${WORKFLOW_PATH}/${id}?workflowName=${name}`);
      Notistack.successMessage('Workflow renamed successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestDeleteWorkflow = (id: string) => {
  return mutate(WORKFLOW_PATH, async() => {
    try {
      await axios.delete(`${WORKFLOW_PATH}/${id}`);
      Notistack.successMessage('Workflow deleted successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestDeleteStep = (id: string) => {
  return mutate(`${WORKFLOW_PATH}/tree`, async() => {
    try {
      await axios.delete(`${STEP_PATH}/${id}`);
      Notistack.successMessage('Step deleted successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestAddStep = (data: IWorkflowStep) => {
  return mutate(STEP_PATH, async() => {
    try {
      await axios.post(`${STEP_PATH}`, data);
      Notistack.successMessage('Step added successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestEditStep = (currentStepId: string, data: IWorkflowStep) => {
  return mutate(STEP_PATH, async() => {
    try {
      await axios.put(`${STEP_PATH}/${currentStepId}`, data);
      Notistack.successMessage('Step edited successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const handleEditParentStep = (currentStepId: string, parentId: string) => {
  return mutate(STEP_PATH, async() => {
    try {
      await axios.put(`${STEP_PATH}/${currentStepId}/parentstep?parentStepId=${parentId}`);
      Notistack.successMessage('Step\'s parent changed successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestCreateReason = (reason: ICreateReasonRequest) => {
  return mutate(STEP_REASON, async() => {
    try {
      await axios.post(STEP_REASON, reason);
      Notistack.successMessage('Reason created successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestUpdateReason = (reasonId: string, reasonData: ICreateReasonRequest) => {
  return mutate(STEP_REASON, async() => {
    try {
      await axios.put(`${STEP_REASON}/${reasonId}`, reasonData);
      Notistack.successMessage('Reason edited successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestDeleteReason = (reasonId: string) => {
  return mutate(DELETE_REASON, async() => {
    try {
      await axios.delete(`${DELETE_REASON}/${reasonId}`);
      Notistack.successMessage('Reason deleted successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestGetReasonsByWorkflowStep = (workflowStepId: string) => {
  const url = `${STEP_PATH}/${workflowStepId}/reasons`;

  return mutate(url, async() => {
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};
